import { Card, Col, Row, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { formatDateInMMDDYYYY } from "../../helpers/date";
import useMultiNotification from "../../hooks/useNotification";
import MiniBodyViewer from "../MiniBodyViewer";
import { getMyOrderDetailsApi } from "../../redux-toolkit/slices/orderCreamSlice";
import { RootState, useAppDispatch } from "../../redux-toolkit/store/store";
import AddressIcon from "./../../graphics/svgs/AddressIcon.svg";
import MailIcon from "./../../graphics/svgs/MailIcon.svg";
import PhoneIcon from "./../../graphics/svgs/PhoneIcon.svg";
import { ReactComponent as RxIcon } from "../../graphics/svgs/rx.svg";
import { orderStatusEnums } from "../../constants/orderConstants";

export default function UserOrderComponent() {
  const generalState = useSelector((state: RootState) => state.general);
  const { singleOrder } = useSelector((state: RootState) => state.orderCream);
  const { isShowTableInDesktop } = generalState;
  const { contextHolder, openNotification } = useMultiNotification();
  const dispatch = useAppDispatch();
  const { orderId } = useParams();
  useEffect(() => {
    dispatch(getMyOrderDetailsApi(`orderId=${orderId}`));
  }, []);

  const painTriggerObj: { [key: string]: string } = {
    at_rest: "At rest",
    being_active: "Being active",
    both: "resting or being active",
    neither: `Pain or resting don't make it worse`,
  };

  const painDailyImpactObj: { [key: string]: string } = {
    none: "Not at all",
    little_bit: "A little bit",
    moderately: "Moderately",
    quite_bit: "Quite a bit",
    extremely: "Extremely",
  };

  const painManagementTimingObj: { [key: string]: string } = {
    am: "Early Morning",
    afternoon: "Midday/Afternoon",
    evening: "Evening/Night",
    allday: "All day",
    varies: "It varies (please explain)",
  };
  const medicineOftenObj: { [key: string]: string } = {
    once: "Once a day",
    twice: "Twice a day",
    three: "Three times a day",
    as_needed: "As needed",
    asneeded: "As needed",
    multiple: "Multiple times a day",
  };

  const medicineDurationObj: { [key: string]: string } = {
    less_week: "Less than a week",
    less_month: "Less than a month",
    less_5_months: "Less than 5 months",
    less_year: "Less than a year",
    year_plus: "1 year +",
  };
  const painManagementGoalObj: { [key: string]: string } = {
    other: `Other (please specify)`,
    sleep: `To improve my sleep quality`,
    medication: `  To reduce reliance on pain medication`,
    activities: ` To return to physical activities or hobbies (e.g., walking,
                gardening, sports)`,
    dailytasks: ` To be able to perform daily tasks currently limited by my pain
                (e.g., cooking, cleaning)`,
    strength: " To improve my strength and flexibility",
  };

  const getPreviouslyDiagnosed = (medicalHistory: MedicalHistory) => {
    let history = medicalHistory.medicalHistory.filter(
      (item) => item !== "Other"
    );
    for (const item of medicalHistory.others) {
      history.push(`${item.name} - ${item.type}`);
    }
    return history;
  };
  return (
    <>
      {contextHolder}

      {singleOrder && (
        <div className="common-section">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center py-8">
              {/* Masonry Layout: Patient Information, Prescribed Formula, Comprehensive Health Assessment, Pain Story & Location */}
              <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ gridAutoRows: 'max-content' }}>
                {/* Patient Information */}
                <div className="login-form-container rounded-3xl sm:rounded-[32px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-12 overflow-hidden relative w-full" style={{ backgroundColor: "var(--neutral-100)" }}>
                  <div className="header-content mb-6" style={{ textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3 className="h3 mb-2" style={{ textAlign: 'left' }}>
                        {singleOrder.shippingDetails.firstName}{" "}
                        {singleOrder.shippingDetails.lastName}
                      </h3>
                      <Typography 
                        className="h6 text-theme-text-secondary" 
                        style={{ 
                          border: '1px solid var(--neutral-600)',
                          padding: '6px 16px',
                          borderRadius: '9999px',
                          lineHeight: '1',
                          display: 'inline-block',
                          textAlign: 'left'
                        }}
                      >
                        Details for Order #{singleOrder.processingDetails.orderNumber}
                      </Typography>
                    </div>
                    <div style={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      backgroundColor: 'var(--neutral-200)',
                      padding: '4px 12px',
                      borderRadius: '9999px',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: 'var(--theme-text-primary)'
                    }}>
                      <span 
                        style={{
                          width: '6px',
                          height: '6px',
                          backgroundColor: 'var(--primary-500)',
                          borderRadius: '50%',
                          animation: 'pulse 2s infinite'
                        }}
                      ></span>
                      {singleOrder.processingDetails.orderStatus}
                    </div>
                  </div>

                  <Row className="w-100p" gutter={[16, 16]}>
                    <Col
                      className="mb-3"
                      lg={8}
                      xl={8}
                      xxl={8}
                      md={12}
                      sm={24}
                      xs={24}
                    >
                      <div className="flex items-start" style={{ gap: '8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginTop: '2px', flexShrink: 0 }}>
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="var(--theme-text-secondary)"/>
                        </svg>
                        <Typography className="body1" style={{ lineHeight: '1.4' }}>
                          {singleOrder.shippingDetails.address.street1}
                          {singleOrder.shippingDetails.address.street2 && `, ${singleOrder.shippingDetails.address.street2}`}, {singleOrder.shippingDetails.address.city}, {singleOrder.shippingDetails.address.state} {singleOrder.shippingDetails.address.zipCode}
                        </Typography>
                      </div>
                    </Col>
                    <Col
                      className="mb-3"
                      lg={8}
                      xl={8}
                      xxl={8}
                      md={12}
                      sm={24}
                      xs={24}
                    >
                      <div className="flex items-start" style={{ gap: '8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginTop: '2px', flexShrink: 0 }}>
                          <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="var(--theme-text-secondary)"/>
                        </svg>
                        <Typography className="body1" style={{ lineHeight: '1.4' }}>
                          {singleOrder.shippingDetails.phoneNumber}
                        </Typography>
                      </div>
                    </Col>
                    <Col
                      className="mb-3"
                      lg={8}
                      xl={8}
                      xxl={8}
                      md={24}
                      sm={24}
                      xs={24}
                    >
                      <div style={{ lineHeight: '1.4' }}>
                        <Typography className="body1 mb-1">
                          DOB: {singleOrder.demoGraphics.dob}
                        </Typography>
                        <Typography className="body1">
                          {singleOrder.demoGraphics.gender.charAt(0).toUpperCase() + singleOrder.demoGraphics.gender.slice(1).toLowerCase()}
                          {singleOrder.demoGraphics.pregnant && (
                            <span className="ml-1 font-semibold">- Pregnant</span>
                          )}, {singleOrder.demoGraphics.weight} lb, {singleOrder.demoGraphics.heightFeet}' {singleOrder.demoGraphics.heightInches}"
                        </Typography>
                      </div>
                    </Col>
                  </Row>
                  
                  <div className="flex justify-start mb-6">
                    <svg viewBox="0 0 122 10" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-[92px] md:w-[122px]">
                      <path
                        opacity="0.4"
                        d="M1.46484 6.83613L4.45387 3.7103C7.74598 0.267505 13.38 0.760513 16.0241 4.72277L16.5428 5.50001C19.2423 9.54539 25.1877 9.54539 27.8873 5.5V5.5C30.5869 1.45461 36.5322 1.45461 39.2318 5.5V5.5C41.9314 9.54539 47.8768 9.54539 50.5764 5.5V5.5C53.2759 1.45461 59.2213 1.45461 61.9209 5.5V5.5C64.6205 9.54539 70.5658 9.54539 73.2654 5.5V5.5C75.965 1.45461 81.9104 1.45461 84.61 5.5V5.5C87.3096 9.54539 93.2549 9.54539 95.9545 5.5V5.5C98.6541 1.45461 104.599 1.45461 107.299 5.5V5.5C109.999 9.54539 115.944 9.54539 118.644 5.5L120.534 2.66667"
                        stroke="var(--primary-500)"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>

                  <div className="mt-6">
                    <div className="mb-6">
                      {singleOrder.additionalInfo && singleOrder.additionalInfo.trim() ? (
                        <>
                          <Typography className="h6 text-theme-text-secondary mb-2">
                            Notes to MD:
                          </Typography>
                          <Typography className="body1" style={{ lineHeight: '1.5' }}>
                            {singleOrder.additionalInfo}
                          </Typography>
                        </>
                      ) : (
                        <Typography className="h6 text-theme-text-secondary">
                          You did not add any additional notes to your provider
                        </Typography>
                      )}
                    </div>
                    <div>
                      {singleOrder.demoGraphics.allAllergies && 
                       Array.isArray(singleOrder.demoGraphics.allAllergies) && 
                       singleOrder.demoGraphics.allAllergies.length > 0 && 
                       singleOrder.demoGraphics.allAllergies.some(item => item && item.type) ? (
                        <>
                          <Typography className="h6 text-theme-text-secondary mb-2">
                            Allergies and skin sensitivities:
                          </Typography>
                          <div style={{ lineHeight: '1.5' }}>
                            {singleOrder.demoGraphics.allAllergies
                              .filter(item => item && item.type)
                              .map((item, index, filteredArray) => (
                                <span key={Math.random()} className="body1">
                                  {item.type} - {item.severity}
                                  {index < filteredArray.length - 1 && ", "}
                                </span>
                              ))}
                          </div>
                        </>
                      ) : (
                        <Typography className="h6 text-theme-text-secondary">
                          No allergies or skin sensitivities were reported
                        </Typography>
                      )}
                    </div>
                  </div>
                </div>

                {/* Prescribed Formula */}
                <div className="login-form-container rounded-3xl sm:rounded-[32px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-12 overflow-hidden relative w-full" style={{ backgroundColor: "var(--neutral-100)" }}>
                    <div className="mb-4" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <h4 className="h4 mb-0">
                        {singleOrder.shippingDetails.firstName}, based on your assessment you were prescribed the following formula:
                      </h4>
                      <div style={{ 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '50%', 
                        border: '1px solid var(--theme-text-secondary)', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center' 
                      }}>
                        <RxIcon style={{ width: '12px', height: '15px' }} />
                      </div>
                    </div>

                    <div className="flex items-start gap-6">
                      <div className="flex-1">
                      <div style={{ color: 'var(--theme-text-primary)' }}>
                        {singleOrder.processingDetails.formulaAssigned !== null ? (
                          <>
                            {singleOrder.processingDetails.formulaAssigned?.activeIngredients?.split(',').map((ingredient, index) => (
                              <p key={index} className="h5" style={{ marginBottom: index === 0 ? '1px' : '0' }}>
                                {ingredient.trim()}
                              </p>
                            ))}
                            <p className="h5" style={{ marginTop: '1px' }}>
                              {singleOrder.processingDetails.formulaAssigned?.creamBase}
                            </p>
                          </>
                        ) : (
                          <p className="h5">---</p>
                        )}
                      </div>
                      
                      {/* Instructions */}
                      {singleOrder.processingDetails.instructions && (
                        <div className="mt-3">
                          <p className="caption1" style={{ color: 'var(--theme-text-primary)' }}>
                            <span className="h6" style={{ color: 'var(--theme-text-secondary)' }}>
                              Directions for use:&nbsp;
                            </span>
                            {singleOrder.processingDetails.instructions}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>


                {/* Comprehensive Health Assessment */}
                <div className="login-form-container rounded-3xl sm:rounded-[32px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-12 overflow-hidden relative w-full" style={{ backgroundColor: "var(--neutral-100)" }}>
                  <div className="header-content mb-6">
                    <h4 className="h4 mb-2" style={{ textAlign: 'left' }}>{singleOrder.shippingDetails.firstName} Health Assessment</h4>
                  </div>

                  <div className="flex justify-start mb-6">
                    <svg viewBox="0 0 122 10" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-[92px] md:w-[122px]">
                      <path
                        opacity="0.4"
                        d="M1.46484 6.83613L4.45387 3.7103C7.74598 0.267505 13.38 0.760513 16.0241 4.72277L16.5428 5.50001C19.2423 9.54539 25.1877 9.54539 27.8873 5.5V5.5C30.5869 1.45461 36.5322 1.45461 39.2318 5.5V5.5C41.9314 9.54539 47.8768 9.54539 50.5764 5.5V5.5C53.2759 1.45461 59.2213 1.45461 61.9209 5.5V5.5C64.6205 9.54539 70.5658 9.54539 73.2654 5.5V5.5C75.965 1.45461 81.9104 1.45461 84.61 5.5V5.5C87.3096 9.54539 93.2549 9.54539 95.9545 5.5V5.5C98.6541 1.45461 104.599 1.45461 107.299 5.5V5.5C109.999 9.54539 115.944 9.54539 118.644 5.5L120.534 2.66667"
                        stroke="var(--primary-500)"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>

                  <div className="flex flex-col gap-5 w-full">
                    {/* MEDICAL BACKGROUND SECTION */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
                        <Typography className="h5 text-theme-text-primary">Medical Background</Typography>
                      </div>
                      
                      <div className="pl-4 space-y-2">
                        {/* Medical History */}
                        <div className="space-y-0.5">
                          <Typography className="h6 text-theme-text-secondary mb-1">Medical History</Typography>
                          <Typography className="body1">
                            {(() => {
                              const mainConditions = singleOrder.medicalHistory.medicalHistory.filter(item => item !== "Other");
                              const hasOtherConditions = singleOrder.medicalHistory.others && singleOrder.medicalHistory.others.length > 0;
                              const additionalConditions = hasOtherConditions 
                                ? singleOrder.medicalHistory.others.map(entry => entry.name).filter(name => name && name.trim() !== "")
                                : [];
                              
                              if (mainConditions.length === 0 && additionalConditions.length === 0) {
                                return <>No significant conditions reported</>;
                              }
                              
                              if (additionalConditions.length === 0) {
                                return <>{mainConditions.join(", ")} and none other reported</>;
                              } else {
                                return <>{mainConditions.join(", ")} and {additionalConditions.join(", ")} reported</>;
                              }
                            })()}
                          </Typography>
                        </div>
                        {/* Previous Surgery */}
                        <div className="space-y-0.5">
                          <Typography className="h6 text-theme-text-secondary mb-1">Previous surgery?</Typography>
                          <Typography className="body1">
                            {singleOrder.injuryHistory.injuryEntryChange === "yes" ? (
                              <>Indicated yes</>
                            ) : singleOrder.injuryHistory.injuryEntryChange === "no" ? (
                              <>Indicated no</>
                            ) : (
                              <>Not specified</>
                            )}
                          </Typography>
                        </div>
                        {/* Injury History Details */}
                        {singleOrder.injuryHistory.injuryEntryChange === "yes" && singleOrder.injuryHistory.injuryEntries.length > 0 && (
                          <div className="space-y-0.5">
                            <Typography className="h6 text-theme-text-secondary mb-1">Injury/Surgery Details</Typography>
                            <Typography className="body1">
                              {singleOrder.injuryHistory.injuryEntries.map((entry, index) => (
                                <div key={index}>
                                  - {entry.name} ({formatDateInMMDDYYYY(entry.date)}) - {entry.details}
                                </div>
                              ))}
                            </Typography>
                          </div>
                        )}

                        {/* Medication History */}
                        <div className="space-y-0.5">
                          <Typography className="h6 text-theme-text-secondary mb-1">Current Medications</Typography>
                          {singleOrder.medicineHistory.medicineDescriptions.includes("None") ? (
                            <Typography className="body1">No medications</Typography>
                          ) : (
                            <div className="space-y-2">
                              {/* Prescription Medicine */}
                              {singleOrder.medicineHistory.medicineDescriptions.includes("Prescription Medicine") && (
                                <div>
                                  <Typography className="body1 font-medium">Prescription Medicine:</Typography>
                                  {singleOrder.medicineHistory.medicineDetails && singleOrder.medicineHistory.medicineDetails.length > 0 ? (
                                    <div className="ml-4">
                                      {singleOrder.medicineHistory.medicineDetails.map((med, index) => (
                                        <Typography key={index} className="body1">
                                          • {med.drugName} {med.strength} ({med.form}) - {medicineOftenObj[med.usageFreq] || med.usageFreq} - {medicineDurationObj[med.duration] || med.duration}
                                        </Typography>
                                      ))}
                                    </div>
                                  ) : (
                                    <Typography className="body1 ml-4">• No specific prescription details provided</Typography>
                                  )}
                                </div>
                              )}
                              
                              {/* Over-the-Counter Products */}
                              {singleOrder.medicineHistory.medicineDescriptions.includes("Over-the-Counter Products") && (
                                <div>
                                  <Typography className="body1 font-medium">Over-the-Counter Products:</Typography>
                                  {singleOrder.medicineHistory.otcMedicineHistory && singleOrder.medicineHistory.otcMedicineHistory.length > 0 ? (
                                    <div className="ml-4">
                                      {singleOrder.medicineHistory.otcMedicineHistory.map((med, index) => (
                                        <Typography key={index} className="body1">
                                          • {med.drugName} {med.strength} ({med.form}) - {medicineOftenObj[med.usageFreq] || med.usageFreq} - {medicineDurationObj[med.duration] || med.duration}
                                        </Typography>
                                      ))}
                                    </div>
                                  ) : (
                                    <Typography className="body1 ml-4">• No specific OTC details provided</Typography>
                                  )}
                                </div>
                              )}
                              
                              {/* Other medication types (just text) */}
                              {singleOrder.medicineHistory.medicineDescriptions
                                .filter(item => item !== "Prescription Medicine" && item !== "Over-the-Counter Products" && item !== "Other")
                                .map((medType, index) => (
                                  <Typography key={index} className="body1">
                                    {medType}
                                  </Typography>
                                ))}
                              
                              {/* Other medication details */}
                              {singleOrder.medicineHistory.medicineDescriptions.includes("Other") && singleOrder.medicineHistory.otherDetails && singleOrder.medicineHistory.otherDetails.trim() !== "" && (
                                <div>
                                  <Typography className="body1 font-medium">Other:</Typography>
                                  <Typography className="body1 ml-4">• {singleOrder.medicineHistory.otherDetails}</Typography>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* PAIN TRIGGERS & PATTERNS SECTION */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
                        <Typography className="h5 text-theme-text-primary">Pain Triggers & Patterns</Typography>
                      </div>
                      
                      <div className="pl-4 space-y-2">
                        {/* Pain Triggers */}
                        <div className="space-y-0.5">
                          <Typography className="h6 text-theme-text-secondary mb-1">Pain Triggers</Typography>
                          {singleOrder.painFactors.painWorseDescriptions.length === 0 || singleOrder.painFactors.painWorseDescriptions.includes("None of these") ? (
                            <Typography className="body1">None identified</Typography>
                          ) : (
                            <div className="space-y-0.5">
                              {singleOrder.painFactors.painWorseDescriptions.includes("Movement") && (
                                <Typography className="body1">Movement triggers: Pain worsens with physical activity</Typography>
                              )}
                              {singleOrder.painFactors.painWorseDescriptions.includes("Pressure on the area") && (
                                <Typography className="body1">Pressure sensitivity: Pain increases with pressure or touch</Typography>
                              )}
                              {singleOrder.painFactors.painWorseDescriptions.includes("Cold weather") && (
                                <Typography className="body1">Weather sensitivity: Cold temperatures make pain worse</Typography>
                              )}
                              {singleOrder.painFactors.painWorseDescriptions.includes("Heat") && (
                                <Typography className="body1">Temperature sensitivity: Heat increases discomfort</Typography>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Associated Symptoms */}
                        <div className="space-y-0.5">
                          <Typography className="h6 text-theme-text-secondary mb-1">Associated Symptoms</Typography>
                          <Typography className="body1">
                            {singleOrder.painSymptoms.selectedPainDescriptions.length === 0 ? (
                              <>None reported</>
                            ) : (
                              <>{singleOrder.painSymptoms.selectedPainDescriptions.join(", ")}</>
                            )}
                          </Typography>
                        </div>

                        {/* Pain Pattern */}
                        <div className="space-y-0.5">
                          <Typography className="h6 text-theme-text-secondary mb-1">Pain Frequency:</Typography>
                          <Typography className="body1">
                            {singleOrder.painSymptoms.painFreq === "constant" ? (
                              <>Present constantly throughout the day</>
                            ) : singleOrder.painSymptoms.painFreq === "intermittent" ? (
                              <>Comes and goes in episodes (intermittent)</>
                            ) : singleOrder.painSymptoms.painFreq === "occasional" ? (
                              <>Occasional episodes of pain</>
                            ) : (
                              <>{singleOrder.painSymptoms.painFreq}</>
                            )}
                          </Typography>
                        </div>
                        {/* Pain Management Timing */}
                        <div className="space-y-0.5">
                          <Typography className="h6 text-theme-text-secondary mb-1">Most pain or discomfort occurs</Typography>
                          <Typography className="body1">
                            {singleOrder.painManagement.timing === "am" ? (
                              <>In the morning</>
                            ) : singleOrder.painManagement.timing === "afternoon" ? (
                              <>In the afternoon</>
                            ) : singleOrder.painManagement.timing === "evening" ? (
                              <>In the evening</>
                            ) : singleOrder.painManagement.timing === "allday" ? (
                              <>Throughout the day</>
                            ) : singleOrder.painManagement.timing === "varies" ? (
                              <>During various times</>
                            ) : (
                              <>{singleOrder.painManagement.timing}</>
                            )}
                          </Typography>
                        </div>

                        {/* Pain Management Goals */}
                        <div className="space-y-0.5">
                          <Typography className="h6 text-theme-text-secondary mb-1">What are your primary goals for pain management?</Typography>
                          <Typography className="body1">
                            {(() => {
                              const goalMappings = {
                                "strength": "To improve my strength and flexibility",
                                "dailytasks": "To be able to perform daily tasks currently limited by my pain (e.g., cooking, cleaning)",
                                "activities": "To return to physical activities or hobbies (e.g., walking, gardening, sports)",
                                "medication": "To reduce reliance on pain medication",
                                "sleep": "To improve my sleep quality",
                                "other": "Other (please specify)"
                              };
                              
                              // Handle both single value (string) and multiple values (array)
                              const goals = Array.isArray(singleOrder.painManagement.painGoal) 
                                ? singleOrder.painManagement.painGoal 
                                : [singleOrder.painManagement.painGoal];
                              
                              const selectedGoals = goals
                                .filter(goal => goal && goalMappings[goal as keyof typeof goalMappings])
                                .map(goal => goalMappings[goal as keyof typeof goalMappings]);
                              
                              if (selectedGoals.length === 0) {
                                return <>No specific goals provided</>;
                              }
                              
                              return <>{selectedGoals.join(", ")}</>;
                            })()}
                          </Typography>
                        </div>

                        {/* Application Frequency */}
                        <div className="space-y-0.5">
                          <Typography className="h6 text-theme-text-secondary mb-1">How do you prefer to apply topical treatments?</Typography>
                          <Typography className="body1">
                            {singleOrder.painManagement.applicationFrequency === "multiple" ? (
                              <>Multiple times a day</>
                            ) : singleOrder.painManagement.applicationFrequency === "once" ? (
                              <>Once daily</>
                            ) : singleOrder.painManagement.applicationFrequency === "asneeded" ? (
                              <>As needed</>
                            ) : (
                              <>{singleOrder.painManagement.applicationFrequency}</>
                            )}
                          </Typography>
                        </div>

                        {/* Pain Timing */}
                        <div className="space-y-0.5">
                          <Typography className="h6 text-theme-text-secondary mb-1">Peak Times</Typography>
                          <Typography className="body1">
                            {singleOrder.painSymptoms.painTrigger === "All day" ? (
                              <>Present throughout the entire day</>
                            ) : singleOrder.painSymptoms.painTrigger === "Morning" ? (
                              <>Worst in the morning</>
                            ) : singleOrder.painSymptoms.painTrigger === "Afternoon" ? (
                              <>Worst in the afternoon</>
                            ) : singleOrder.painSymptoms.painTrigger === "Evening" ? (
                              <>Worst in the evening</>
                            ) : singleOrder.painSymptoms.painTrigger === "With movement" ? (
                              <>Occurs with movement</>
                            ) : singleOrder.painSymptoms.painTrigger === "At rest" ? (
                              <>Present during rest</>
                            ) : (
                              <>{painTriggerObj[singleOrder.painSymptoms.painTrigger] || "Not specified"}</>
                            )}
                          </Typography>
                        </div>

                        {/* Daily Impact */}
                        <div className="space-y-0.5">
                          <Typography className="h6 text-theme-text-secondary mb-1">Daily Impact</Typography>
                          <Typography className="body1">
                            {singleOrder.painSymptoms.painDailyImpact === "none" ? (
                              <>No interference with activities</>
                            ) : singleOrder.painSymptoms.painDailyImpact === "little_bit" ? (
                              <>Slight interference with some activities</>
                            ) : singleOrder.painSymptoms.painDailyImpact === "moderately" ? (
                              <>Moderate interference with daily tasks</>
                            ) : singleOrder.painSymptoms.painDailyImpact === "quite_bit" ? (
                              <>Significant interference with most activities</>
                            ) : singleOrder.painSymptoms.painDailyImpact === "extremely" ? (
                              <>Severe interference with all activities</>
                            ) : (
                              <>{painDailyImpactObj[singleOrder.painSymptoms.painDailyImpact] || "Not specified"}</>
                            )}
                          </Typography>
                        </div>

                        {/* Additional Pain Details */}
                        {(singleOrder.painSymptoms.showTriggerTextBox && singleOrder.painSymptoms.painTriggerInfo && singleOrder.painSymptoms.painTriggerInfo.length > 0) ||
                         (singleOrder.painSymptoms.showPainChangeTextBox && singleOrder.painSymptoms.painChangeInfo && singleOrder.painSymptoms.painChangeInfo.length > 0) ||
                         (singleOrder.painSymptoms.showPainSevereTimeTextBox && singleOrder.painSymptoms.painSpecificTimeInDayInfo && singleOrder.painSymptoms.painSpecificTimeInDayInfo.length > 0) ? (
                          <div className="space-y-0.5">
                            <Typography className="h6 text-theme-text-secondary mb-1">Additional Details</Typography>
                            {singleOrder.painSymptoms.showTriggerTextBox === false ? (
                              <Typography className="body1">No noted triggers that exacerbate the pain</Typography>
                            ) : singleOrder.painSymptoms.showTriggerTextBox === true && singleOrder.painSymptoms.painTriggerInfo && singleOrder.painSymptoms.painTriggerInfo.length > 0 ? (
                              <>
                                <Typography className="body1">Noted the following as trigger(s) that exacerbate the pain:</Typography>
                                <Typography className="body1">{singleOrder.painSymptoms.painTriggerInfo}</Typography>
                              </>
                            ) : null}
                            {singleOrder.painSymptoms.showPainChangeTextBox === false ? (
                              <Typography className="body1">Indicated pain does not change throughout the day</Typography>
                            ) : singleOrder.painSymptoms.showPainChangeTextBox === true && singleOrder.painSymptoms.painChangeInfo && singleOrder.painSymptoms.painChangeInfo.length > 0 ? (
                              <>
                                <Typography className="body1">Noted the following on how pain changes throughout the day:</Typography>
                                <Typography className="body1">{singleOrder.painSymptoms.painChangeInfo}</Typography>
                              </>
                            ) : null}
                            {singleOrder.painSymptoms.showPainSevereTimeTextBox === false ? (
                              <Typography className="body1">Noted no specific times when the pain is most severe</Typography>
                            ) : singleOrder.painSymptoms.showPainSevereTimeTextBox === true && singleOrder.painSymptoms.painSpecificTimeInDayInfo && singleOrder.painSymptoms.painSpecificTimeInDayInfo.length > 0 ? (
                              <>
                                <Typography className="body1">Noted the following about specific timing of pain to be most severe:</Typography>
                                <Typography className="body1">{singleOrder.painSymptoms.painSpecificTimeInDayInfo}</Typography>
                              </>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {/* LIFESTYLE CONTEXT SECTION */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
                        <Typography className="h5 text-theme-text-primary">Lifestyle Context</Typography>
                      </div>
                      
                      <div className="pl-4 space-y-2">
                        {/* Activity Level */}
                        <div className="space-y-0.5">
                          <Typography className="h6 text-theme-text-secondary mb-1">Reported Level of Physical Activity</Typography>
                          <Typography className="body1">
                            {singleOrder.painFactors.physicalActivity === "high" ? (
                              <>Very active with regular intense exercise</>
                            ) : singleOrder.painFactors.physicalActivity === "moderate" ? (
                              <>Active with regular moderate exercise</>
                            ) : singleOrder.painFactors.physicalActivity === "light" ? (
                              <>Light physical activity only</>
                            ) : singleOrder.painFactors.physicalActivity === "sedentary" ? (
                              <>Primarily sedentary lifestyle</>
                            ) : (
                              <>{singleOrder.painFactors.physicalActivity}</>
                            )}
                          </Typography>
                        </div>

                        {/* Treatment Goals */}
                        <div className="space-y-0.5">
                          <Typography className="h6 text-theme-text-secondary mb-1">Treatment Goals</Typography>
                          <Typography className="body1">
                            {singleOrder.painManagement.painGoal && singleOrder.painManagement.painGoal.length > 0 ? (
                              <>{painManagementGoalObj[singleOrder.painManagement.painGoal] || singleOrder.painManagement.painGoal}</>
                            ) : (
                              <>No specific goals provided</>
                            )}
                          </Typography>
                        </div>

                        {/* Application Preferences */}
                        <div className="space-y-0.5">
                          <Typography className="h6 text-theme-text-secondary mb-1">Application Preferences</Typography>
                          <Typography className="body1">
                            {singleOrder.painManagement.applicationFrequency === "Once a day" ? (
                              <>Once daily application</>
                            ) : singleOrder.painManagement.applicationFrequency === "Twice a day" ? (
                              <>Twice daily application</>
                            ) : singleOrder.painManagement.applicationFrequency === "As needed" ? (
                              <>Apply as needed for pain relief</>
                            ) : singleOrder.painManagement.applicationFrequency === "Before activities" ? (
                              <>Apply before physical activities</>
                            ) : (
                              <>{medicineOftenObj[singleOrder.painManagement.applicationFrequency] || singleOrder.painManagement.applicationFrequency}</>
                            )}
                          </Typography>
                        </div>

                        {/* Previous Experience */}
                        <div className="space-y-0.5">
                          <Typography className="h6 text-theme-text-secondary mb-1">Previous Experience</Typography>
                          <Typography className="body1">
                            {singleOrder.painManagement.usedBefore === "Yes" || singleOrder.painManagement.usedBefore === "yes" ? (
                              <>
                                Experience with topical treatments before include:
                                {singleOrder.painManagement.experience && singleOrder.painManagement.experience.length > 0 && (
                                  <><br />Experience: {singleOrder.painManagement.experience}</>
                                )}
                              </>
                            ) : singleOrder.painManagement.usedBefore === "No" || singleOrder.painManagement.usedBefore === "no" ? (
                              <>No prior experience with topical treatments</>
                            ) : (
                              <>{singleOrder.painManagement.usedBefore}</>
                            )}
                          </Typography>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>

                {/* Pain Story & Location Visualization */}
                <div className="login-form-container rounded-3xl sm:rounded-[32px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-12 overflow-hidden relative w-full" style={{ backgroundColor: "var(--neutral-100)" }}>
                  <div className="header-content mb-6">
                    <h4 className="h4 mb-2" style={{ textAlign: 'left' }}>Pain Story & Location</h4>
                  </div>

                  <div className="flex justify-start mb-6">
                    <svg viewBox="0 0 122 10" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-[92px] md:w-[122px]">
                      <path
                        opacity="0.4"
                        d="M1.46484 6.83613L4.45387 3.7103C7.74598 0.267505 13.38 0.760513 16.0241 4.72277L16.5428 5.50001C19.2423 9.54539 25.1877 9.54539 27.8873 5.5V5.5C30.5869 1.45461 36.5322 1.45461 39.2318 5.5V5.5C41.9314 9.54539 47.8768 9.54539 50.5764 5.5V5.5C53.2759 1.45461 59.2213 1.45461 61.9209 5.5V5.5C64.6205 9.54539 70.5658 9.54539 73.2654 5.5V5.5C75.965 1.45461 81.9104 1.45461 84.61 5.5V5.5C87.3096 9.54539 93.2549 9.54539 95.9545 5.5V5.5C98.6541 1.45461 104.599 1.45461 107.299 5.5V5.5C109.999 9.54539 115.944 9.54539 118.644 5.5L120.534 2.66667"
                        stroke="var(--primary-500)"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>

                  <div className="flex flex-col gap-3 w-full">
                    {/* Pain Story Content */}
                    <div className="space-y-2">
                      {/* Pain Rating Display */}
                      <div>
                        <Typography className="body1">
                          {singleOrder.painProfile.painRating === 0 ? (
                            <>Current pain level: 0/10 - No pain</>
                          ) : singleOrder.painProfile.painRating >= 1 && singleOrder.painProfile.painRating <= 3 ? (
                            <>Current pain level: {singleOrder.painProfile.painRating}/10 - Mild pain</>
                          ) : singleOrder.painProfile.painRating >= 4 && singleOrder.painProfile.painRating <= 6 ? (
                            <>Current pain level: {singleOrder.painProfile.painRating}/10 - Moderate pain</>
                          ) : singleOrder.painProfile.painRating >= 7 && singleOrder.painProfile.painRating <= 8 ? (
                            <>Current pain level: {singleOrder.painProfile.painRating}/10 - Severe pain</>
                          ) : singleOrder.painProfile.painRating >= 9 && singleOrder.painProfile.painRating <= 10 ? (
                            <>Current pain level: {singleOrder.painProfile.painRating}/10 - Extreme pain</>
                          ) : (
                            <>Current pain level: {singleOrder.painProfile.painRating}/10</>
                          )}
                        </Typography>
                      </div>

                      {/* Pain Duration Display */}
                      <div>
                        <Typography className="body1">
                          {singleOrder.painProfile.painDuration === "Acute - less than 3 months" ? (
                            <>Pain started: Less than 3 months ago</>
                          ) : singleOrder.painProfile.painDuration === "Chronic - 3-6 months" ? (
                            <>Pain started: 3-6 months ago</>
                          ) : singleOrder.painProfile.painDuration === "Chronic - 6+ months" ? (
                            <>Pain started: Over 6 months ago</>
                          ) : singleOrder.painProfile.painDuration === "Chronic - 1+ years" ? (
                            <>Pain started: More than 1 year ago</>
                          ) : singleOrder.painProfile.painDuration === "acute" ? (
                            <>Pain started: Less than 3 months ago</>
                          ) : (
                            <>Pain started: More than 3 months ago</>
                          )}
                        </Typography>
                      </div>

                      {/* Pain Descriptions Display */}
                      <div>
                        <Typography className="body1">
                          {singleOrder.painProfile.painDescriptions.length === 1 ? (
                            <>How it feels: {singleOrder.painProfile.painDescriptions[0]} sensation</>
                          ) : singleOrder.painProfile.painDescriptions.length > 1 ? (
                            <>How it feels: {singleOrder.painProfile.painDescriptions.slice(0, -1).join(", ")}, and {singleOrder.painProfile.painDescriptions[singleOrder.painProfile.painDescriptions.length - 1]} sensations</>
                          ) : (
                            <>How it feels: No specific descriptions provided</>
                          )}
                        </Typography>
                      </div>

                      {/* Pain Location Display */}
                      <div>
                        <Typography className="body1">
                          {singleOrder.painProfile.painLocations.length === 1 ? (
                            <>Pain location: {singleOrder.painProfile.painLocations[0]}</>
                          ) : singleOrder.painProfile.painLocations.length > 1 ? (
                            <>Pain locations: {singleOrder.painProfile.painLocations.join(", ")}</>
                          ) : (
                            <>Pain location: No specific locations provided</>
                          )}
                        </Typography>
                      </div>

                      {/* Open Wound Check */}
                      <div>
                        <Typography className="body1">
                          {singleOrder.injuryHistory.hasOpenWound === "No" || singleOrder.injuryHistory.hasOpenWound === "no" ? (
                            <>Wound check: No open wounds at pain site</>
                          ) : singleOrder.injuryHistory.hasOpenWound === "Yes" || singleOrder.injuryHistory.hasOpenWound === "yes" ? (
                            <>⚠️ Wound check: Open wound present - requires evaluation</>
                          ) : (
                            <>Wound check: No information provided</>
                          )}
                        </Typography>
                      </div>
                    </div>

                    {/* Visual Separator */}
                    <div className="border-t border-gray-200 my-3"></div>

                    {/* Pain Location Visualization */}
                    <div className="text-center">
                      <Typography className="h5 mb-3">
                        Pain Location Visualization
                      </Typography>
                    </div>
                    <div className="flex justify-center">
                      <MiniBodyViewer isBodyClickEnable={false} painLocations={singleOrder?.painProfile?.painLocations || []} />
                    </div>



                    {/* Clinical Findings and Symptom Alerts */}
                    <div className="space-y-2">
                      {singleOrder.painSymptoms.selectedPainDescriptions.includes("Muscle spasms") && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <Typography className="body1">
                            <strong>• Muscle Spasms:</strong> Indicated interferes with sleep and accompanied by stiffness or weakness.
                          </Typography>
                        </div>
                      )}
                      {singleOrder.painDetails && singleOrder.painDetails.impactedJoints === "yes" && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <Typography className="body1">
                            <strong>• Joint Involvement:</strong> Specific joints are affected by the pain condition.
                          </Typography>
                        </div>
                      )}
                      {singleOrder.painDetails && singleOrder.painDetails.swelling === "yes" && (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <Typography className="body1">
                            <strong>• Inflammation:</strong> Swelling or inflammation is present and associated with the pain.
                          </Typography>
                        </div>
                      )}
                      {singleOrder.painDetails && singleOrder.painDetails.sleepInterference === "yes" && (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <Typography className="body1">
                            <strong>• Sleep Disruption:</strong> Spasms interfere with sleep quality and rest.
                          </Typography>
                        </div>
                      )}
                      {singleOrder.painDetails && singleOrder.painDetails.burningSensation === "yes" && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <Typography className="body1">
                            <strong>• Neuropathic Symptoms:</strong> Burning or tingling sensation present, indicating potential nerve involvement.
                          </Typography>
                        </div>
                      )}
                      {singleOrder.painDetails && singleOrder.painDetails.specificStimuli === "yes" && (
                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                          <Typography className="body1">
                            <strong>• Stimulus Sensitivity:</strong> Pain responds to specific stimuli such as light touch, indicating heightened sensitivity.
                          </Typography>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
